/*
 * Pre-checkout modal. Intercepts elements marked [data-precheckout-trigger] and
 * gathers per-product info before letting the native checkout flow proceed.
 *
 * Field definitions are NOT hardcoded here — they come from Shopify metaobjects
 * (`checkout_field` type) referenced by two product metafields:
 *   custom.required_checkout_fields  → emitted by Liquid with "required": true
 *   custom.optional_checkout_fields  → emitted by Liquid with "required": false
 * See md/dynamic-checkout-fields-architecture.md.
 *
 * Pass-through contract: after the modal submits successfully, the original
 * trigger is re-clicked with data-precheckout-cleared="1" so the native handler
 * (form submit / navigation) runs unmodified.
 */
(function () {
  'use strict';

  const SECTION_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/') + '?section_id=pre-checkout-data';

  const CART_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/') + 'cart';

  // Field `key` values that must NEVER be drafted to sessionStorage (PII guard).
  // Passport is the only one today; add more here if a new sensitive field is introduced.
  const DRAFT_EXCLUDED_KEYS = new Set(['passport']);

  // Field `key` values that should be enhanced with Google Places Autocomplete.
  // Hardcoded here (rather than driven by metaobject input_type) so existing field
  // definitions don't need editing. Add a new key here to enable autocomplete on it.
  const PLACES_AUTOCOMPLETE_KEYS = new Set(['hotel_pickup']);

  // Short-lived in-memory cache for the section payload.
  // Avoids hitting the Section Rendering API on every modal open (and avoids 429s on rapid
  // re-opens). Invalidated explicitly after persist() modifies the cart, and also via the
  // Dawn cartUpdate PubSub event so any external cart change (add, remove, qty tweak) is
  // reflected immediately the next time the modal opens.
  const PAYLOAD_CACHE = { data: null, ts: 0, itemCount: -1, ttl: 45000 };

  // Subscribe to Dawn's cart-update broadcast so removing an item from cart
  // (or any other external mutation) invalidates the cache immediately.
  // Without this, a stale 45-second cache would still show the removed product
  // asking for booking details on the next modal open.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      PAYLOAD_CACHE.data = null;
    });
  }

  // Lazy, idempotent loader for the Google Maps JS API. Called the first time a
  // Places-enabled input is rendered; subsequent calls return the cached promise so
  // the script tag is only injected once per page. Uses `loading=async` so we can
  // dynamic-import libraries on demand via `google.maps.importLibrary(...)`.
  // Resolves to true on success, false if the key is missing or the script fails to
  // load — callers must gracefully fall back to a plain text input in the false case.
  let googleMapsPromise = null;
  function loadGoogleMapsScript(apiKey, language) {
    if (googleMapsPromise) return googleMapsPromise;
    if (!apiKey) {
      googleMapsPromise = Promise.resolve(false);
      return googleMapsPromise;
    }
    if (window.google && window.google.maps && typeof window.google.maps.importLibrary === 'function') {
      googleMapsPromise = Promise.resolve(true);
      return googleMapsPromise;
    }
    googleMapsPromise = new Promise((resolve) => {
      const callbackName = '__precheckoutGmapsReady_' + Date.now();
      window[callbackName] = function () {
        try {
          delete window[callbackName];
        } catch (_) {
          window[callbackName] = undefined;
        }
        resolve(!!(window.google && window.google.maps && typeof window.google.maps.importLibrary === 'function'));
      };
      const script = document.createElement('script');
      const params = new URLSearchParams({
        key: apiKey,
        loading: 'async',
        callback: callbackName,
        v: 'weekly'
      });
      if (language) params.set('language', language);
      script.src = 'https://maps.googleapis.com/maps/api/js?' + params.toString();
      script.async = true;
      script.onerror = () => {
        console.warn('[pre-checkout-modal] Google Maps script failed to load — hotel autocomplete disabled.');
        resolve(false);
      };
      document.head.appendChild(script);
    });
    return googleMapsPromise;
  }

  // Attach hotel-pickup autocomplete to a text input using the programmatic
  // AutocompleteSuggestion API (Places API New). Keeps the original Dawn .field input
  // fully visible and focused — no mirror, no web component, no focus hand-off.
  // Requests are debounced at 300 ms; a single AutocompleteSessionToken spans from
  // first keystroke to place selection, so billing is one session regardless of how
  // many debounced requests fire within it.
  // Dropdown is appended to document.body (position:fixed) so the modal's
  // overflow:hidden / overflow-y:auto containers never clip it.
  async function attachPlacesAutocomplete(input) {
    if (!window.google || !window.google.maps || typeof window.google.maps.importLibrary !== 'function') return;
    if (input.dataset.precheckoutPlacesAttached === '1') return;

    let AutocompleteSuggestion, AutocompleteSessionToken;
    try {
      ({ AutocompleteSuggestion, AutocompleteSessionToken } =
        await window.google.maps.importLibrary('places'));
    } catch (err) {
      console.warn('[pre-checkout-modal] Places library failed to load', err);
      return;
    }
    if (!AutocompleteSuggestion || !AutocompleteSessionToken) return;
    if (input.dataset.precheckoutPlacesAttached === '1') return;
    input.dataset.precheckoutPlacesAttached = '1';
    input.setAttribute('autocomplete', 'off');

    const language = (input.closest('pre-checkout-modal') || {}).dataset
      ? (input.closest('pre-checkout-modal').dataset.googleMapsLanguage || 'en')
      : 'en';

    let sessionToken = new AutocompleteSessionToken();
    let debounceTimer = null;
    let liveSuggestions = [];
    let activeIdx = -1;
    let skipNextFetch = false; // suppresses the fetch triggered by pick()'s programmatic input dispatch

    // --- Dropdown element ---
    // Append to <pre-checkout-modal> (position:fixed; inset:0 = full viewport).
    // position:absolute inside a full-viewport fixed container gives the same
    // viewport-relative coordinates as position:fixed on body, but the dropdown
    // is now part of the modal's DOM — it disappears when the modal is hidden and
    // never bleeds outside the modal's visual boundary.
    const modalEl = input.closest('pre-checkout-modal') || document.body;
    const dropdown = document.createElement('ul');
    dropdown.className = 'pre-checkout-modal__places-dropdown';
    dropdown.setAttribute('role', 'listbox');
    dropdown.hidden = true;
    modalEl.appendChild(dropdown);

    // --- Loading indicator ---
    // Sits inside the field so it tracks the input, and covers the whole wait:
    // the 300 ms debounce AND the network round trip. Showing it only for the
    // request would leave the first third of the pause with no feedback.
    const spinner = document.createElement('span');
    spinner.className = 'pre-checkout-modal__places-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    spinner.hidden = true;
    (input.closest('.field') || input.parentElement || modalEl).appendChild(spinner);

    // Two independent reasons to be busy, tracked separately so a slow response
    // arriving after the user has typed again cannot hide the spinner early.
    let awaitingDebounce = false;
    let inflight = 0;

    function syncSpinner() {
      spinner.hidden = !(awaitingDebounce || inflight > 0);
    }

    // Screen readers get the status; sighted users get the spinner.
    input.setAttribute('aria-busy', 'false');
    function setAriaBusy() {
      input.setAttribute('aria-busy', spinner.hidden ? 'false' : 'true');
    }

    function placeDropdown() {
      const anchor = input.closest('.pre-checkout-modal__field-row') || input;
      const r = anchor.getBoundingClientRect();
      const dialog = input.closest('.pre-checkout-modal__dialog');
      const dr = dialog ? dialog.getBoundingClientRect() : null;

      // Close if the input has scrolled outside the modal's visible area.
      if (dr && (r.bottom <= dr.top + 4 || r.top >= dr.bottom - 4)) {
        closeDropdown();
        return;
      }

      // Constrain the list to the VIEWPORT, not to the dialog.
      //
      // The dropdown is appended to <pre-checkout-modal> as a SIBLING of
      // .pre-checkout-modal__dialog and carries z-index 10, so it paints over
      // the dialog and nothing clips it — it has the whole viewport to use.
      // Measuring against dr.bottom instead starved it: the hotel-pickup field
      // is the last row in the dialog, so `dr.bottom - r.bottom` collapsed to
      // roughly zero, the MIN_H floor forced an 80px box out anyway, and the
      // customer was left scrolling a sliver that showed one and a half
      // results. That is the "dropdown is hidden / how can we search like
      // this" report.
      //
      // The scroll guard above still uses dr, which is correct: it closes the
      // list when the INPUT scrolls out of the modal's visible area. That is a
      // different question from how tall the list may be.
      const GUTTER = 8; // keeps the list off the viewport edge
      const availBelow = Math.max(0, window.innerHeight - r.bottom - GUTTER);
      const availAbove = Math.max(0, r.top - GUTTER);
      const IDEAL_H = 220; // matches CSS max-height (22rem)
      const MIN_H = 80;    // don't show a dropdown smaller than this

      dropdown.style.left = r.left + 'px';
      dropdown.style.width = r.width + 'px';

      if (availBelow >= MIN_H || availBelow >= availAbove) {
        // Show below the input, capped to available dialog space.
        dropdown.style.top = r.bottom + 'px';
        dropdown.style.maxHeight = Math.min(IDEAL_H, Math.max(MIN_H, availBelow)) + 'px';
      } else if (availAbove >= MIN_H) {
        // Flip above the input.
        const h = Math.min(IDEAL_H, availAbove);
        dropdown.style.top = (r.top - h) + 'px';
        dropdown.style.maxHeight = h + 'px';
      } else {
        // Virtually no space — close rather than show a tiny unusable dropdown.
        closeDropdown();
      }
    }

    function renderDropdown(suggestions) {
      liveSuggestions = suggestions;
      activeIdx = -1;
      dropdown.innerHTML = '';
      suggestions.forEach(function (s, i) {
        // AutocompleteSuggestion can be a place OR a query prediction.
        // Skip non-place entries — accessing .mainText on null throws and the
        // catch block above silently swallows it, making the whole dropdown vanish.
        const p = s.placePrediction;
        if (!p) return;

        // FormattableText exposes the string via .text, not .toString().
        const mainStr = p.mainText ? (p.mainText.text || '') : (p.text ? (p.text.text || '') : '');
        const secStr = p.secondaryText ? (p.secondaryText.text || '') : '';

        const li = document.createElement('li');
        li.className = 'pre-checkout-modal__places-option';
        li.setAttribute('role', 'option');
        li.dataset.idx = i;

        const main = document.createElement('span');
        main.className = 'pre-checkout-modal__places-option-main';
        main.textContent = mainStr;
        li.appendChild(main);

        if (secStr) {
          const sec = document.createElement('span');
          sec.className = 'pre-checkout-modal__places-option-secondary';
          sec.textContent = secStr;
          li.appendChild(sec);
        }

        li.addEventListener('mousedown', function (e) {
          e.preventDefault(); // keep input focus
          pick(i);
        });
        dropdown.appendChild(li);
      });

      // Use childElementCount rather than suggestions.length — some suggestions
      // may be skipped (non-place), so the rendered count can be less.
      if (dropdown.childElementCount > 0) {
        placeDropdown();
        dropdown.hidden = false;
      } else {
        dropdown.hidden = true;
      }
    }

    function closeDropdown() {
      dropdown.hidden = true;
      liveSuggestions = [];
      activeIdx = -1;
    }

    function highlight(idx) {
      dropdown.querySelectorAll('.pre-checkout-modal__places-option').forEach(function (el, i) {
        el.classList.toggle('is-active', i === idx);
      });
      activeIdx = idx;
    }

    async function pick(idx) {
      const s = liveSuggestions[idx];
      if (!s || !s.placePrediction) return;
      closeDropdown();
      // A debounce from the last keystroke may still be armed; selecting a
      // suggestion settles the field, so cancel it and clear the spinner
      // rather than letting a now-pointless lookup fire.
      clearTimeout(debounceTimer);
      awaitingDebounce = false;
      syncSpinner();
      setAriaBusy();
      const p = s.placePrediction;
      const fallbackText = p.text ? (p.text.text || '') : '';
      try {
        const place = p.toPlace();
        // Fetch both displayName and formattedAddress — cheapest fetchFields SKU.
        // Adding geometry/photos/etc. jumps to a more expensive billing tier.
        await place.fetchFields({ fields: ['displayName', 'formattedAddress'] });
        const name = place.displayName || '';
        const addr = place.formattedAddress || '';
        input.value = name && addr ? name + ', ' + addr : (addr || name || fallbackText);
      } catch (_) {
        input.value = fallbackText;
      }
      // Raise the flag BEFORE dispatching so the input listener's debounce is
      // skipped — without this the programmatic dispatch re-triggers the fetch,
      // the dropdown reopens with the same value, and the user gets a loop.
      skipNextFetch = true;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      sessionToken = new AutocompleteSessionToken(); // start a fresh billing session
    }

    // --- Event listeners ---

    input.addEventListener('input', function () {
      if (skipNextFetch) { skipNextFetch = false; return; }
      clearTimeout(debounceTimer);
      const val = input.value.trim();
      if (val.length < 2) {
        closeDropdown();
        awaitingDebounce = false;
        syncSpinner();
        setAriaBusy();
        return;
      }

      awaitingDebounce = true;
      syncSpinner();
      setAriaBusy();

      debounceTimer = setTimeout(async function () {
        awaitingDebounce = false;
        inflight += 1;
        syncSpinner();
        setAriaBusy();
        try {
          const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: val,
            sessionToken: sessionToken,
            includedPrimaryTypes: ['lodging'],
            language: language
          });
          renderDropdown(result.suggestions || []);
        } catch (err) {
          console.warn('[pre-checkout-modal] autocomplete fetch failed', err);
          closeDropdown();
        } finally {
          inflight = Math.max(0, inflight - 1);
          syncSpinner();
          setAriaBusy();
        }
      }, 300);
    });

    input.addEventListener('keydown', function (e) {
      if (dropdown.hidden) return;
      const n = liveSuggestions.length;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        highlight((activeIdx + 1) % n);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlight((activeIdx - 1 + n) % n);
      } else if (e.key === 'Enter' && activeIdx >= 0) {
        e.preventDefault();
        pick(activeIdx);
      } else if (e.key === 'Escape') {
        closeDropdown();
      }
    });

    // Delay so a mousedown selection fires before blur closes the dropdown.
    input.addEventListener('blur', function () {
      setTimeout(closeDropdown, 150);
    });

    // Close on modal-body scroll (input moves, dropdown can't follow in absolute coords)
    // and reposition on window resize.
    const bodyEl = input.closest('.pre-checkout-modal__body');
    if (bodyEl) bodyEl.addEventListener('scroll', closeDropdown, { passive: true });
    window.addEventListener('resize', placeDropdown, { passive: true });
  }

  function passengerPropertyName(field, index) {
    const base = field.property_name + ' (Traveler ' + index + ')';
    return field.is_private ? '_' + base : base;
  }

  function linePropertyName(field) {
    return field.is_private ? '_' + field.property_name : field.property_name;
  }

  // Pull the passenger category out of a Shopify variant title (e.g. "Adult(10+) / Without VIP" -> "Adult").
  // Falls back to null so the caller can use a generic "Traveler" label.
  function passengerType(variantTitle) {
    if (!variantTitle) return null;
    const match = variantTitle.match(/(Adult|Child|Infant|Senior|Youth|Student|Teen)/i);
    return match ? match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase() : null;
  }

  // Draft persistence — sessionStorage, scoped by cart token. Used so the user can close the modal
  // mid-flow and not lose typing. Passport (and any other DRAFT_EXCLUDED_KEYS entry) is opted out.
  const DRAFT_PREFIX = 'precheckout-draft:';

  function draftKey(token, parts) {
    return DRAFT_PREFIX + (token || 'anon') + ':' + parts.join(':');
  }

  function draftRead(token, parts) {
    try {
      return sessionStorage.getItem(draftKey(token, parts)) || '';
    } catch (_) {
      return '';
    }
  }

  function draftWrite(token, parts, value) {
    try {
      const k = draftKey(token, parts);
      if (value) sessionStorage.setItem(k, value);
      else sessionStorage.removeItem(k);
    } catch (_) {}
  }

  function draftClearAll() {
    try {
      const toDelete = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.indexOf(DRAFT_PREFIX) === 0) toDelete.push(k);
      }
      toDelete.forEach((k) => sessionStorage.removeItem(k));
    } catch (_) {}
  }

  class PreCheckoutModal extends HTMLElement {
    constructor() {
      super();
      this.i18n = this.safeParseI18n();
      this.form = this.querySelector('[data-precheckout-form]');
      this.body = this.querySelector('[data-precheckout-body]');
      this.errorEl = this.querySelector('[data-precheckout-error]');
      this.dialog = this.querySelector('[role="dialog"]');
      this.submitBtn = this.querySelector('[data-precheckout-submit]');
      this.submitLabel = this.querySelector('[data-precheckout-submit-label]');
      this.opener = null;
      // Per-line cache of the current payload's fields[], keyed by line key. Populated on
      // each open() so collectPerLineProperties() can look up a field by key without
      // re-querying the DOM dataset.
      this.fieldsByLine = {};
      this.propertiesByLine = {}; // existing cart-line properties at payload-fetch time, used as merge base in persist()
      this.quantityByLine = {};   // existing cart-line quantities — passed explicitly to /cart/change.js so Shopify doesn't silently default to 1
      this.bindStaticEvents();
    }

    safeParseI18n() {
      try {
        return JSON.parse(this.dataset.i18n || '{}');
      } catch (_) {
        return {};
      }
    }

    t(key) {
      return this.i18n[key] || key;
    }

    bindStaticEvents() {
      // X button — intentional close.
      this.querySelectorAll('[data-precheckout-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          this.close();
        });
      });
      // Overlay — swallow the click so it doesn't bleed through to the cart
      // drawer / cart notification underneath, but do NOT close the modal.
      // Customers have no other route to see their cart; accidental overlay
      // taps must not lose their form progress.
      const overlay = this.querySelector('[data-precheckout-overlay]');
      if (overlay) overlay.addEventListener('click', (e) => e.stopPropagation());
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.close();
        }
      });
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
      // "Save & View cart" button — partial-save then navigate (no validation required).
      const viewCartBtn = this.querySelector('[data-precheckout-view-cart]');
      if (viewCartBtn) viewCartBtn.addEventListener('click', () => this.saveAndViewCart(viewCartBtn));
    }

    async saveAndViewCart(btn) {
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        const lbl = btn.querySelector('[data-precheckout-view-cart-label]');
        if (lbl) lbl.textContent = this.t('submitting');
      }
      try {
        // Partial save: only write fields that have a value so existing data isn't cleared.
        const lines = this.collectPerLineProperties(true);
        const sequential = Object.keys(lines).map((key) => () => this.updateLine(key, this.quantityByLine[key] || 1, lines[key]));
        for (const fn of sequential) await fn();
        PAYLOAD_CACHE.data = null; // cart modified — invalidate
        draftClearAll();
      } catch (_) {
        // Best-effort: navigate to cart even if the save failed (network blip).
      }
      window.location.href = CART_URL;
    }

    async open(opener, scope) {
      this.opener = opener;
      this.lastScope = scope;
      this.isEditMode = !!(scope && scope.editMode);
      this.isRetryMode = false;
      this.clearError();
      this.setSubmitting(false);
      this.body.innerHTML = '';
      this.submitLabel.textContent = this.isEditMode ? this.t('save') : this.t('submit');
      this.fieldsByLine = {};
      this.propertiesByLine = {};
      this.quantityByLine = {};

      // The cart-notification stays open behind the modal — its document.body click
      // listener is patched in assets/cart-notification.js to ignore clicks inside
      // `pre-checkout-modal`, so the notification no longer self-closes when the
      // customer interacts with the modal.

      // If the modal is already open (retry-from-error scenario), show a loading
      // skeleton in the body so the customer has visual feedback during retries.
      if (this.classList.contains('is-open')) {
        this.body.innerHTML = '<div class="pre-checkout-modal__loading-skeleton" role="status" aria-label="Loading…"></div>';
      }

      let payload;
      try {
        payload = await this.fetchPayloadWithRetry();
      } catch (err) {
        console.error('[pre-checkout-modal] payload fetch failed after retries', err);
        this.isRetryMode = true;
        this.body.innerHTML = '';
        this.showError(this.t('error_load'));
        this.submitLabel.textContent = this.t('retry');
        this.reveal();
        return;
      }
      this.body.innerHTML = ''; // clear skeleton before rendering form

      this.cartToken = payload.cart_token || '';

      // Defensive dedup — if the same checkout_field record is referenced from both
      // `custom.required_checkout_fields` and `custom.optional_checkout_fields` on a
      // product (or twice in the same list), the Liquid payload emits it twice and
      // the modal would render duplicate inputs. Collapse by `key`, preferring the
      // `required: true` copy so a required field never silently downgrades to optional.
      payload.items.forEach((item) => {
        if (!Array.isArray(item.fields)) return;
        const seen = new Map();
        for (const f of item.fields) {
          if (!f || !f.key) continue;
          const existing = seen.get(f.key);
          if (!existing || (f.required && !existing.required)) seen.set(f.key, f);
        }
        item.fields = Array.from(seen.values());
      });

      const itemsMissing = payload.items.filter((i) => this.hasMissingInfo(i));
      const itemsWithAnyFields = payload.items.filter(
        (i) => Array.isArray(i.fields) && i.fields.length > 0
      );

      // Reveal the floating edit FAB whenever the cart has items with checkout fields.
      // Enable it only when at least one item already has saved property data (meaning
      // the customer has gone through the booking engine or modal at least once).
      // This handles the post-AJAX-add-to-cart case where the Liquid-rendered FAB was
      // hidden/disabled because the item wasn't in the cart at page load.
      const hasSavedData = itemsWithAnyFields.some((i) => {
        const props = i.properties || {};
        return Object.keys(props).some((k) => props[k]);
      });
      this.revealFab(itemsWithAnyFields.length > 0, hasSavedData);

      // In normal mode: pass straight through when no required fields are missing.
      // In edit mode: we always want to show the form, so skip this short-circuit.
      if (!this.isEditMode && itemsMissing.length === 0) {
        // Diagnostic: surfaces the most common cause of "modal closes immediately" — products
        // in the cart don't have `custom.required_checkout_fields` populated, so there's
        // nothing required to ask. Inspect each item's `fields` array; an empty array means
        // the product's metafields are not set (or not pointing at a `checkout_field` record).
        console.info(
          '[pre-checkout-modal] No required fields missing — skipping modal and proceeding to checkout.',
          {
            itemCount: payload.items.length,
            itemsWithFields: itemsWithAnyFields.length,
            itemsBreakdown: payload.items.map((i) => ({
              title: i.title,
              variant: i.variant_title,
              fieldsCount: Array.isArray(i.fields) ? i.fields.length : 0,
              requiredFieldKeys: Array.isArray(i.fields)
                ? i.fields.filter((f) => f.required).map((f) => f.key)
                : [],
              optionalFieldKeys: Array.isArray(i.fields)
                ? i.fields.filter((f) => !f.required).map((f) => f.key)
                : []
            }))
          }
        );
        this.passThrough();
        return;
      }

      // Edit mode with no fields at all — nothing to show, bail silently.
      if (this.isEditMode && itemsWithAnyFields.length === 0) return;

      // Cache fields for property-write lookup, and snapshot existing cart-line
      // properties for ALL items so persist() can merge instead of overwrite.
      // Without the merge, updateLine() would wipe booking-engine properties
      // (Booking Date, Starting Hour, etc.) that were never in the modal form.
      payload.items.forEach((i) => {
        this.propertiesByLine[i.key] = i.properties || {};
        this.quantityByLine[i.key] = i.quantity || 1;
        if (Array.isArray(i.fields) && i.fields.length > 0) {
          this.fieldsByLine[i.key] = i.fields;
        }
      });

      // Edit mode renders ALL items with fields so the customer can review/correct any value.
      // Normal mode renders only items that are still missing required info.
      const candidates = this.isEditMode ? itemsWithAnyFields : itemsMissing;
      const renderItems = this.narrowScope(candidates, scope);
      this.renderForm(renderItems);
      this.reveal();
    }

    narrowScope(itemsMissing, scope) {
      if (!scope || !scope.itemKey) return itemsMissing;
      const justAdded = itemsMissing.find((i) => i.key === scope.itemKey);
      if (justAdded && itemsMissing.length === 1) return [justAdded];
      return itemsMissing;
    }

    hasMissingInfo(item) {
      if (!Array.isArray(item.fields) || item.fields.length === 0) return false;
      const props = item.properties || {};
      // Only required fields block checkout. Optional fields are rendered alongside but
      // never gate passThrough().
      const requiredFields = item.fields.filter((f) => f.required);
      for (const field of requiredFields) {
        if (field.scope === 'line') {
          if (!props[linePropertyName(field)]) return true;
        } else if (field.scope === 'passenger') {
          for (let p = 1; p <= item.quantity; p++) {
            if (!props[passengerPropertyName(field, p)]) return true;
          }
        }
      }
      return false;
    }

    async fetchPayload() {
      // Serve from cache when fresh and cart size hasn't changed — prevents 429s on
      // rapid re-opens and eliminates redundant Section Rendering API calls.
      const now = Date.now();
      if (
        PAYLOAD_CACHE.data &&
        now - PAYLOAD_CACHE.ts < PAYLOAD_CACHE.ttl
      ) {
        return PAYLOAD_CACHE.data;
      }

      const res = await fetch(SECTION_URL, { headers: { Accept: 'text/html' }, cache: 'no-store' });

      // 429 = rate limited. Throw with retryAfter so the retry loop can wait the right amount.
      if (res.status === 429) {
        const retryAfterMs = Math.max(
          5000,
          parseInt(res.headers.get('Retry-After') || '5', 10) * 1000
        );
        const err = new Error('rate limited (429)');
        err.retryAfter = retryAfterMs;
        throw err;
      }

      if (!res.ok) throw new Error('payload http ' + res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.getElementById('precheckout-payload');
      if (!node) {
        // Useful when Shopify returns 200 but the section template threw a Liquid error
        // or the response was truncated — first 200 chars usually identify it.
        console.warn('[pre-checkout-modal] #precheckout-payload not found. Response snippet:', html.slice(0, 200));
        throw new Error('payload missing');
      }

      let payload;
      try {
        payload = JSON.parse(node.textContent);
      } catch (parseErr) {
        console.warn('[pre-checkout-modal] payload JSON parse failed. Raw:', node.textContent.slice(0, 300));
        throw parseErr;
      }

      // Store in cache. Invalidated when item_count changes or after persist().
      PAYLOAD_CACHE.data = payload;
      PAYLOAD_CACHE.ts = Date.now();
      PAYLOAD_CACHE.itemCount = payload.item_count;
      return payload;
    }

    async fetchPayloadWithRetry() {
      // 5 attempts: immediate + 4 retries. Total worst-case wait ~15 s.
      // For 429 responses the server's Retry-After value overrides the scheduled backoff.
      const backoffMs = [0, 1000, 2000, 4000, 7000];
      let lastErr;
      for (let i = 0; i < backoffMs.length; i++) {
        const wait = (lastErr && lastErr.retryAfter)
          ? Math.max(backoffMs[i] || 0, lastErr.retryAfter)
          : (backoffMs[i] || 0);
        if (wait > 0) await new Promise((r) => setTimeout(r, wait));
        try {
          return await this.fetchPayload();
        } catch (err) {
          lastErr = err;
          console.warn('[pre-checkout-modal] payload fetch attempt ' + (i + 1) + ' failed:', err.message || err);
        }
      }
      throw lastErr;
    }

    renderForm(items) {
      const frag = document.createDocumentFragment();
      const sharedHotel = this.shouldOfferSharedPickup(items);

      if (sharedHotel) frag.appendChild(this.renderSharedPickupBlock(items));

      items.forEach((item) => {
        frag.appendChild(this.renderItemBlock(item, { excludeHotel: sharedHotel }));
      });

      this.body.appendChild(frag);
      this.wireSharedToggle();

      const first = this.body.querySelector('input, textarea, select');
      if (first) first.focus();
    }

    shouldOfferSharedPickup(items) {
      if (items.length < 2) return false;
      // Offer the shared-pickup affordance only when every item has hotel_pickup wired up.
      return items.every(
        (i) => Array.isArray(i.fields) && i.fields.some((f) => f.key === 'hotel_pickup')
      );
    }

    renderSharedPickupBlock(items) {
      const wrapper = document.createElement('section');
      wrapper.className = 'pre-checkout-modal__shared';
      wrapper.dataset.precheckoutShared = 'hotel_pickup';

      // Use the first item's hotel_pickup field as the template. The metaobject is the same
      // record across products, so its definition is identical wherever it appears.
      const hotelField = this.findFieldAcrossItems(items, 'hotel_pickup');
      if (!hotelField) return wrapper;

      const heading = document.createElement('h3');
      heading.className = 'pre-checkout-modal__shared-title h3';
      heading.textContent = this.t('shared_pickup_label');
      wrapper.appendChild(heading);

      const field = this.buildFieldInput(hotelField, {
        idSuffix: 'shared-hotel',
        prefill: this.findSharedPrefill(items, hotelField),
        persistKey: ['shared', 'hotel_pickup']
      });
      wrapper.appendChild(field);

      const toggleLabel = document.createElement('label');
      toggleLabel.className = 'pre-checkout-modal__shared-toggle';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.dataset.precheckoutSharedToggle = '1';
      toggleLabel.appendChild(cb);
      toggleLabel.appendChild(document.createTextNode(' ' + this.t('shared_pickup_toggle')));
      wrapper.appendChild(toggleLabel);

      return wrapper;
    }

    findFieldAcrossItems(items, fieldKey) {
      for (const item of items) {
        if (!Array.isArray(item.fields)) continue;
        const f = item.fields.find((x) => x.key === fieldKey);
        if (f) return f;
      }
      return null;
    }

    findSharedPrefill(items, field) {
      const propName = linePropertyName(field);
      const values = items
        .map((i) => (i.properties && i.properties[propName]) || '')
        .filter(Boolean);
      const allSame = values.length === items.length && values.every((v) => v === values[0]);
      return allSame ? values[0] : '';
    }

    wireSharedToggle() {
      const toggle = this.querySelector('[data-precheckout-shared-toggle]');
      if (!toggle) return;
      const sharedBlock = this.querySelector('[data-precheckout-shared="hotel_pickup"]');
      const sharedRow = sharedBlock ? sharedBlock.querySelector('.pre-checkout-modal__field-row') : null;
      const perItemHotelRows = this.querySelectorAll('[data-precheckout-line-hotel]');

      const apply = () => {
        const perItem = toggle.checked;
        if (sharedRow) {
          sharedRow.hidden = perItem;
          sharedRow.toggleAttribute('aria-hidden', perItem);
        }
        perItemHotelRows.forEach((el) => {
          el.hidden = !perItem;
          el.toggleAttribute('aria-hidden', !perItem);
        });
      };
      toggle.addEventListener('change', apply);
      apply();
    }

    renderItemBlock(item, opts) {
      opts = opts || {};
      const wrapper = document.createElement('section');
      wrapper.className = 'pre-checkout-modal__item';
      wrapper.dataset.precheckoutLine = item.key;

      wrapper.appendChild(this.renderItemHeader(item));

      const fields = Array.isArray(item.fields) ? item.fields : [];
      const lineFields = fields.filter((f) => f.scope === 'line');
      const passengerFields = fields.filter((f) => f.scope === 'passenger');

      lineFields.forEach((f) => {
        const isHotelSharable = f.key === 'hotel_pickup' && opts.excludeHotel;
        const input = this.buildFieldInput(f, {
          idSuffix: item.key + '-' + f.key,
          prefill: (item.properties || {})[linePropertyName(f)] || '',
          persistKey: ['line', item.key, f.key]
        });
        input.dataset.precheckoutLineField = f.key;
        if (isHotelSharable) {
          input.dataset.precheckoutLineHotel = '1';
          input.hidden = true;
          input.setAttribute('aria-hidden', 'true');
        }
        wrapper.appendChild(input);
      });

      const type = passengerType(item.variant_title);
      for (let p = 1; p <= item.quantity; p++) {
        if (passengerFields.length === 0) break;
        const group = document.createElement('div');
        group.className = 'pre-checkout-modal__traveler';
        const label = document.createElement('h4');
        label.className = 'pre-checkout-modal__traveler-title h4';
        label.textContent = type
          ? this.t('passenger_type_label').replace('{type}', type).replace('{n}', p)
          : this.t('traveler_label').replace('{n}', p);
        group.appendChild(label);

        passengerFields.forEach((f) => {
          const input = this.buildFieldInput(f, {
            idSuffix: item.key + '-' + f.key + '-' + p,
            prefill: (item.properties || {})[passengerPropertyName(f, p)] || '',
            persistKey: ['pax', item.key, f.key, String(p)]
          });
          input.dataset.precheckoutPassengerIndex = p;
          input.dataset.precheckoutPassengerField = f.key;
          group.appendChild(input);
        });

        wrapper.appendChild(group);
      }

      return wrapper;
    }

    renderItemHeader(item) {
      const header = document.createElement('header');
      header.className = 'pre-checkout-modal__item-header';
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 64;
        img.height = 64;
        img.className = 'pre-checkout-modal__item-image';
        header.appendChild(img);
      }
      const titleWrap = document.createElement('div');
      const title = document.createElement('p');
      title.className = 'pre-checkout-modal__item-title h4';
      title.textContent = item.title;
      titleWrap.appendChild(title);
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const variant = document.createElement('p');
        variant.className = 'pre-checkout-modal__item-variant caption';
        variant.textContent = item.variant_title;
        titleWrap.appendChild(variant);
      }
      const qty = document.createElement('p');
      qty.className = 'pre-checkout-modal__item-qty caption';
      qty.textContent = '× ' + item.quantity;
      titleWrap.appendChild(qty);
      header.appendChild(titleWrap);
      return header;
    }

    buildFieldInput(field, opts) {
      const id = 'pcm-' + opts.idSuffix.replace(/[^a-z0-9-]/gi, '');
      const errorId = id + '-error';

      const row = document.createElement('div');
      row.className = 'pre-checkout-modal__field-row';

      const fieldWrapper = document.createElement('div');
      fieldWrapper.className = 'field';

      const isTextarea = field.input_type === 'textarea';
      const input = isTextarea ? document.createElement('textarea') : document.createElement('input');
      input.id = id;
      input.className = 'field__input';
      // Only required fields enforce HTML5 required validation. Optional fields are rendered
      // but the customer can leave them blank and still proceed to checkout.
      input.required = !!field.required;
      input.dataset.precheckoutInput = '1';
      input.dataset.precheckoutFieldKey = field.key;
      input.placeholder = ' ';
      input.setAttribute('aria-describedby', errorId);

      const canPersist =
        !DRAFT_EXCLUDED_KEYS.has(field.key) && Array.isArray(opts.persistKey);
      let initial = opts.prefill || '';
      if (!initial && canPersist) initial = draftRead(this.cartToken, opts.persistKey);
      if (initial) input.value = initial;

      if (canPersist) {
        const token = this.cartToken;
        const parts = opts.persistKey;
        input.addEventListener('input', () => {
          draftWrite(token, parts, input.value.trim());
        });
      }

      if (field.input_type === 'number') {
        input.type = 'number';
        input.inputMode = 'numeric';
        if (field.min_value !== null && field.min_value !== undefined) input.min = field.min_value;
        if (field.max_value !== null && field.max_value !== undefined) input.max = field.max_value;
      } else if (!isTextarea) {
        // text / tel / email — fall back to text if metaobject sends something unknown.
        const allowed = ['text', 'tel', 'email'];
        input.type = allowed.indexOf(field.input_type) !== -1 ? field.input_type : 'text';
        if (field.max_length) input.maxLength = field.max_length;
        if (field.validation_pattern) input.pattern = field.validation_pattern;
      }

      const label = document.createElement('label');
      label.htmlFor = id;
      label.className = 'field__label';
      label.textContent = field.label;

      fieldWrapper.appendChild(input);
      fieldWrapper.appendChild(label);

      const error = document.createElement('p');
      error.id = errorId;
      error.className = 'pre-checkout-modal__field-error';
      error.hidden = true;
      error.setAttribute('aria-live', 'polite');

      row.appendChild(fieldWrapper);
      row.appendChild(error);

      // Google Places enhancement — lazy-loads the Maps JS API on the input's FIRST
      // focus (not on render). This avoids billing for "abandoned" modal opens where
      // the customer never interacts with the hotel field. If the script fails or no
      // API key is configured the input silently stays a plain text field (validation,
      // persist, draft all keep working).
      if (PLACES_AUTOCOMPLETE_KEYS.has(field.key) && !isTextarea && input.type !== 'number') {
        const apiKey = this.dataset.googleMapsApiKey || '';
        const language = this.dataset.googleMapsLanguage || '';
        if (apiKey) {
          const armPlaces = () => {
            input.removeEventListener('focus', armPlaces);
            input.removeEventListener('pointerdown', armPlaces);
            loadGoogleMapsScript(apiKey, language).then((ready) => {
              if (ready && input.isConnected) attachPlacesAutocomplete(input);
            });
          };
          // `pointerdown` fires slightly before `focus`, giving the script a tiny head
          // start on mouse/touch input. `focus` covers keyboard-tab users.
          input.addEventListener('focus', armPlaces, { once: true });
          input.addEventListener('pointerdown', armPlaces, { once: true });
        }
      }

      return row;
    }

    findFieldOnLine(lineKey, fieldKey) {
      const fields = this.fieldsByLine[lineKey];
      if (!Array.isArray(fields)) return null;
      return fields.find((f) => f.key === fieldKey) || null;
    }

    async handleSubmit(e) {
      e.preventDefault();

      if (this.isRetryMode) {
        this.open(this.opener, this.lastScope);
        return;
      }

      this.clearError();

      const invalid = this.validateAll();
      if (invalid) {
        invalid.focus({ preventScroll: true });
        invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      this.setSubmitting(true);
      try {
        await this.persist();
        if (this.isEditMode) {
          // Edit mode: details saved — just close, don't navigate to checkout.
          this.close();
        } else {
          this.passThrough();
        }
      } catch (err) {
        this.setSubmitting(false);
        this.showError(this.t('error_generic'));
      }
    }

    validateAll() {
      const inputs = Array.from(this.querySelectorAll('[data-precheckout-input]'));
      let firstInvalid = null;

      for (const input of inputs) {
        const errorEl = this.findErrorEl(input);
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }

        if (input.hidden || input.closest('[hidden]')) continue;
        if (input.checkValidity()) continue;

        const lineEl = input.closest('[data-precheckout-line]');
        const lineKey = lineEl ? lineEl.dataset.precheckoutLine : null;
        const fieldKey = input.dataset.precheckoutFieldKey;
        const field = lineKey
          ? this.findFieldOnLine(lineKey, fieldKey)
          : this.findFieldAcrossCachedLines(fieldKey);
        const message = this.errorMessageFor(input, field);
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
        if (!firstInvalid) firstInvalid = input;
      }

      return firstInvalid;
    }

    findFieldAcrossCachedLines(fieldKey) {
      // Used for the shared-pickup input (not attached to a specific line).
      const lineKeys = Object.keys(this.fieldsByLine);
      for (const lk of lineKeys) {
        const f = this.findFieldOnLine(lk, fieldKey);
        if (f) return f;
      }
      return null;
    }

    findErrorEl(input) {
      const row = input.closest('.pre-checkout-modal__field-row');
      return row ? row.querySelector('.pre-checkout-modal__field-error') : null;
    }

    errorMessageFor(input, field) {
      const v = input.validity;
      if (v.valueMissing) return this.t('error_field_required');
      if (v.patternMismatch && field && field.pattern_error_msg) return field.pattern_error_msg;
      if ((v.rangeUnderflow || v.rangeOverflow) && field && field.range_error_msg) return field.range_error_msg;
      return this.t('error_field_invalid');
    }

    async persist() {
      const lines = this.collectPerLineProperties();
      const sequential = Object.keys(lines).map((key) => () => this.updateLine(key, this.quantityByLine[key] || 1, lines[key]));
      for (const fn of sequential) {
        await fn();
      }
      // Drafts are no longer needed once values are saved to the cart (source of truth).
      draftClearAll();
      // Cart was modified — force a fresh payload on the next open().
      PAYLOAD_CACHE.data = null;
      // Data is now saved — enable the FAB so the customer can edit again.
      this.revealFab(true, true);
    }

    collectPerLineProperties(partial) {
      const byLine = {};
      const sharedHotelInput = this.querySelector('[data-precheckout-shared="hotel_pickup"] [data-precheckout-input]');
      const sharedToggle = this.querySelector('[data-precheckout-shared-toggle]');
      const useShared = sharedHotelInput && (!sharedToggle || !sharedToggle.checked);

      this.querySelectorAll('[data-precheckout-line]').forEach((lineEl) => {
        const key = lineEl.dataset.precheckoutLine;
        // Start from the existing cart-line properties (captured at modal-open time).
        // This preserves booking-engine data (Booking Date, Starting Hour, etc.) and
        // any previously-saved pre-checkout fields not shown in the current form.
        // Form values overlay these keys; anything not in the form is kept as-is.
        byLine[key] = { ...(this.propertiesByLine[key] || {}) };

        lineEl.querySelectorAll('[data-precheckout-line-field]').forEach((wrapper) => {
          const input = wrapper.querySelector('[data-precheckout-input]');
          const fieldKey = wrapper.dataset.precheckoutLineField;
          const field = this.findFieldOnLine(key, fieldKey);
          if (!field) return;
          const value = (fieldKey === 'hotel_pickup' && useShared)
            ? sharedHotelInput.value.trim()
            : input.value.trim();
          // partial=true: skip empty values so existing saved data isn't overwritten by blanks
          if (!partial || value) byLine[key][linePropertyName(field)] = value;
        });

        lineEl.querySelectorAll('[data-precheckout-passenger-field]').forEach((wrapper) => {
          const input = wrapper.querySelector('[data-precheckout-input]');
          const fieldKey = wrapper.dataset.precheckoutPassengerField;
          const idx = parseInt(wrapper.dataset.precheckoutPassengerIndex, 10);
          const field = this.findFieldOnLine(key, fieldKey);
          if (!field || !idx) return;
          const value = input.value.trim();
          if (!partial || value) byLine[key][passengerPropertyName(field, idx)] = value;
        });
      });

      return byLine;
    }

    async updateLine(lineKey, quantity, properties) {
      const res = await fetch(CART_URL + '/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity, properties: properties })
      });
      if (!res.ok) throw new Error('cart change http ' + res.status);
      return res.json();
    }

    passThrough() {
      this.close();
      // Navigate directly rather than re-clicking the opener. Re-clicking with
      // precheckoutCleared=1 lets our interceptor return early but does NOT call
      // stopImmediatePropagation(), so the booking engine's checkout listener runs
      // and re-syncs the cart from the current form inputs (quantity=1 default),
      // overwriting the customer's actual quantities.
      const checkoutUrl =
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + 'checkout'
          : '/checkout';
      window.location.href = checkoutUrl;
    }

    reveal() {
      this.hidden = false;
      // Lock scroll on both <html> and <body> — the actual scroll root varies
      // by browser (Chrome scrolls <html>, Safari scrolls <body>), so we lock both.
      document.documentElement.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      if (typeof trapFocus === 'function') trapFocus(this, this.dialog);
      requestAnimationFrame(() => {
        this.classList.add('is-open');
      });
    }

    close() {
      this.classList.remove('is-open');
      this.hidden = true;
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
    }

    setSubmitting(on) {
      this.submitBtn.classList.toggle('loading', on);
      this.submitBtn.disabled = on;
      this.submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
      this.submitLabel.textContent = on ? this.t('submitting') : (this.isEditMode ? this.t('save') : this.t('submit'));
      const spinner = this.submitBtn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !on);
    }

    showError(msg) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    revealFab(show, hasData) {
      const fab = document.getElementById('PreCheckoutEditFab');
      if (!fab) return;
      fab.hidden = !show;
      // Enable/disable only when caller provides an explicit opinion.
      if (hasData !== undefined) fab.disabled = !hasData;
    }

    clearError() {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  customElements.define('pre-checkout-modal', PreCheckoutModal);

  function getModal() {
    return document.getElementById('PreCheckoutModal');
  }

  function getNotificationScope() {
    const node = document.querySelector('cart-notification');
    const key = node && node.cartItemKey;
    return key ? { itemKey: key } : null;
  }

  function setTriggerLoading(trigger, loading) {
    if (!trigger) return;
    if (loading) {
      trigger.setAttribute('aria-busy', 'true');
      trigger.classList.add('loading');
    } else {
      trigger.removeAttribute('aria-busy');
      trigger.classList.remove('loading');
    }
    const spinner = trigger.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  document.addEventListener(
    'click',
    async function (e) {
      const trigger = e.target.closest('[data-precheckout-trigger]');
      if (!trigger) return;
      // Defensive: another script (e.g. product-form during multi-variant add) may have
      // marked the trigger aria-disabled while cart writes are in flight. Don't open then.
      if (trigger.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      // Already opening from a previous click — swallow this one.
      if (trigger.getAttribute('aria-busy') === 'true') {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      const modal = getModal();
      if (!modal) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const scopeMode = trigger.dataset.precheckoutScope;
      const editMode = trigger.dataset.precheckoutMode === 'edit';
      let scope = scopeMode === 'single' ? getNotificationScope() : null;
      if (editMode) scope = { ...(scope || {}), editMode: true };

      // Hold the trigger in a loading state until modal.open() finishes — covers the
      // fetch + retries so the customer can't reach a half-loaded modal during the brief
      // window where the cart was just written and Shopify's section render hasn't caught up.
      setTriggerLoading(trigger, true);
      try {
        await modal.open(trigger, scope);
      } finally {
        setTriggerLoading(trigger, false);
      }
    },
    true
  );
})();
